/**
 * 主题分享链接：单个主题 /topic/<编号>，多选清单 /list/<编号>,<编号>...?t=<标题>
 * 编号用主题 id（uuid）去掉横线后的前 8 位；和别的主题撞上时自动加长，保证唯一。
 * 前后端共用，服务端按同样的规则反查主题。
 */

export const SHARE_CODE_MIN_LENGTH = 8;
export const SHARE_LIST_MAX_TOPICS = 20;
export const SHARE_LIST_TITLE_MAX_LENGTH = 30;

const SHARE_CODE_PATTERN = /^[0-9a-f]{8,32}$/;

const compactId = (id: string) => id.toLowerCase().replace(/-/g, '');

/** 为 id 生成在 allIds 里唯一的最短编号（至少 8 位） */
export function buildShareCode(id: string, allIds: readonly string[]): string {
  const target = compactId(id);
  const others = allIds.map(compactId).filter((other) => other !== target);
  for (let length = SHARE_CODE_MIN_LENGTH; length < target.length; length += 1) {
    const prefix = target.slice(0, length);
    if (!others.some((other) => other.startsWith(prefix))) return prefix;
  }
  return target;
}

export function isValidShareCode(code: string): boolean {
  return SHARE_CODE_PATTERN.test(code);
}

/** 在一组主题里找编号对应的那一个；找不到或不唯一都返回 undefined */
export function findByShareCode<T extends { id: string }>(code: string, items: readonly T[]): T | undefined {
  const normalized = code.toLowerCase();
  if (!isValidShareCode(normalized)) return undefined;
  const matches = items.filter((item) => compactId(item.id).startsWith(normalized));
  return matches.length === 1 ? matches[0] : undefined;
}

export function normalizeShareListTitle(title: string | undefined | null): string {
  return Array.from((title || '').replace(/\s+/g, ' ').trim()).slice(0, SHARE_LIST_TITLE_MAX_LENGTH).join('');
}

/** 解析清单路径里的编号：去重、去掉不合法的，最多 20 个 */
export function parseShareListCodes(raw: string): string[] {
  const codes: string[] = [];
  for (const part of raw.split(',')) {
    const code = part.trim().toLowerCase();
    if (isValidShareCode(code) && !codes.includes(code)) codes.push(code);
    if (codes.length >= SHARE_LIST_MAX_TOPICS) break;
  }
  return codes;
}

export function buildTopicSharePath(code: string): string {
  return `/topic/${code}`;
}

export function buildListSharePath(codes: readonly string[], title?: string): string {
  const path = `/list/${codes.slice(0, SHARE_LIST_MAX_TOPICS).join(',')}`;
  const normalizedTitle = normalizeShareListTitle(title);
  return normalizedTitle ? `${path}?t=${encodeURIComponent(normalizedTitle)}` : path;
}

/** 首页打开某个主题详情用的链接参数：/?topic=<编号> */
export const HOME_TOPIC_QUERY_KEY = 'topic';

/** 分享文案：复制时是“文案 + 链接”，粘贴到微信里直接能发；不写年级（前台还没启用） */
export interface ShareText {
  /** 不含链接的文案，给系统分享面板的 text 用（链接单独传） */
  body: string;
  /** 文案 + 换行 + 链接，复制用 */
  full: string;
}

const withUrl = (body: string, url: string): ShareText => ({ body, full: `${body}\n${url}` });

export function buildTopicShareText(
  topic: { name: string; categoryLabel?: string; duration?: string },
  url: string,
): ShareText {
  const facts = [topic.categoryLabel, topic.duration ? `约 ${topic.duration}` : ''].filter(Boolean).join(' · ');
  const lines = [
    `推荐一个少儿英语演讲主题：「${topic.name}」`,
    facts,
    '有原声示范视频、可编辑 PPT、中英双语文稿，点开就能看示范👇',
  ].filter(Boolean);
  return withUrl(lines.join('\n'), url);
}

export const SHARE_LIST_TEXT_MAX_NAMES = 5;

export function buildListShareText(title: string, names: readonly string[], url: string): ShareText {
  const count = names.length;
  const head = title ? `为你挑选了 ${count} 个英语演讲主题：${title}` : `为你挑选了 ${count} 个英语演讲主题`;
  const shown = names.slice(0, SHARE_LIST_TEXT_MAX_NAMES).join('、');
  const list = count > SHARE_LIST_TEXT_MAX_NAMES ? `${shown} 等 ${count} 个` : shown;
  const lines = [head, list, '每个都有示范视频和 PPT，点开看详情👇'].filter(Boolean);
  return withUrl(lines.join('\n'), url);
}

export function buildSiteShareText(url: string): ShareText {
  return withUrl('上百个少儿英语演讲主题，在线预览示范视频和 PPT，轻松给孩子挑选👇', url);
}
